"use client";

/**
 * Shared visual surface for the two trust workspaces, /search and
 * /wallet-analysis.
 *
 * Both pages previously hard-coded their colours - `backgroundColor:
 * "#020617"`, `color: "#e5e7eb"`, `text-slate-50`, `text-slate-300/70` - so
 * neither responded to the app's own theme toggle, and every panel re-declared
 * the same values. These tokens are defined for BOTH themes in one place and
 * the two pages render this component instead of carrying their own copy.
 *
 * The rules are deliberately unlayered: Tailwind v4.3 emits its own output
 * unlayered too, so `.ts-card` and a utility class tie on specificity and
 * source order decides. This is injected inside the page subtree and therefore
 * wins, which lets a page keep the odd utility without fighting the system.
 */

const TRUST_CSS = `
/* ROOT CAUSE FIX - read this before touching any width below.
   styles/globals.css declares Tailwind with the v3 directives
   (\`@tailwind base;\`), which Tailwind v4 no longer expands, so Preflight is
   never emitted. Verified against the deployed stylesheet: the string
   "box-sizing" appears ZERO times in the whole build, and getComputedStyle on
   the live page reports content-box for <body>, .ts-card, .ts-search and the
   query input alike. (The chain <select> reads border-box only because that is
   Chromium's UA default for <select> - which is exactly why the input grew
   into the select and not the other way round.)
   Under content-box, padding and border are ADDED to a declared width or
   min-height, so .ts-input-lg rendered 42px wider (20+20 padding + 2 border)
   and 34px taller (16+16 + 2) than its grid track, overhanging the chain
   control by 22px at desktop; .ts-portal-item and the dropdown footer button
   (width:100% + horizontal padding) overflowed the dropdown panel the same
   way, which is why the provider list looked broken at every size.
   Every rule in this file - and every Tailwind utility used on these pages -
   is written against the border-box model, so restore it for this subtree and
   for the dropdown, which is portalled to <body> outside .ts-root.
   Scoped rather than global on purpose: turning Preflight on app-wide would
   also reset headings, lists and margins across pages that were visually
   tuned without it. */
.ts-root,.ts-root *,.ts-root *::before,.ts-root *::after,
.ts-portal,.ts-portal *,.ts-portal *::before,.ts-portal *::after{
  box-sizing:border-box;
}

.ts-root{
  --ts-page:radial-gradient(1200px 620px at 12% -6%,rgba(34,197,94,.10),transparent 56%),
            radial-gradient(980px 520px at 88% 4%,rgba(56,189,248,.12),transparent 54%),
            linear-gradient(180deg,#050b18 0%,#03060f 100%);
  --ts-surface:linear-gradient(180deg,rgba(24,35,62,.9),rgba(12,19,36,.96));
  --ts-surface-2:rgba(9,15,30,.55);
  --ts-border:rgba(158,178,214,.18);
  --ts-border-strong:rgba(52,211,153,.34);
  --ts-text:#EAF0FF;
  --ts-text-soft:#C3CCE4;
  --ts-muted:#96A0BC;
  --ts-accent:#34d399;
  --ts-accent-2:#22d3ee;
  --ts-ok:#00F2A6;
  --ts-warn:#FFB800;
  --ts-danger:#FF6070;
  --ts-shadow:0 26px 70px -42px rgba(0,0,0,.95),0 10px 30px -22px rgba(16,185,129,.35);
  min-height:100vh;
  background:var(--ts-page);
  color:var(--ts-text);
}
[data-theme="light"] .ts-root{
  --ts-page:radial-gradient(1100px 560px at 10% -8%,rgba(16,185,129,.10),transparent 56%),
            radial-gradient(900px 480px at 92% 2%,rgba(56,189,248,.10),transparent 54%),
            linear-gradient(180deg,#f5f8fd 0%,#eaf0f9 100%);
  --ts-surface:linear-gradient(180deg,#ffffff,#fbfcff);
  --ts-surface-2:rgba(240,244,252,.9);
  --ts-border:rgba(15,23,42,.11);
  --ts-border-strong:rgba(5,150,105,.34);
  --ts-text:#0B1220;
  --ts-text-soft:#28324A;
  --ts-muted:#5A6580;
  --ts-accent:#059669;
  --ts-accent-2:#0891b2;
  --ts-ok:#0F9D6E;
  --ts-warn:#B57400;
  --ts-danger:#D42A3C;
  --ts-shadow:0 20px 48px -34px rgba(15,23,42,.32);
}

.ts-card{
  border:1px solid var(--ts-border);
  border-radius:24px;
  background:var(--ts-surface);
  box-shadow:var(--ts-shadow);
}
.ts-eyebrow{font-size:.68rem;font-weight:800;letter-spacing:.18em;text-transform:uppercase;color:var(--ts-muted)}
.ts-h1{
  font-size:clamp(2rem,5vw,3.25rem);font-weight:800;letter-spacing:-.02em;line-height:1.05;
  color:var(--ts-text);margin:0;text-wrap:balance;
}
.ts-lede{font-size:clamp(.95rem,2.2vw,1.06rem);line-height:1.6;color:var(--ts-muted);margin:0}
.ts-field{display:block}
.ts-field + .ts-field{margin-top:16px}
.ts-label{display:block;font-size:.68rem;font-weight:800;letter-spacing:.15em;text-transform:uppercase;color:var(--ts-muted);margin-bottom:7px}

.ts-input,.ts-select{
  display:block;width:100%;min-height:52px;padding:13px 16px;
  font-family:inherit;font-size:1rem;line-height:1.4;
  border-radius:14px;border:1px solid var(--ts-border);
  background:var(--ts-surface-2);color:var(--ts-text);
  -webkit-appearance:none;appearance:none;
  transition:border-color .16s ease,box-shadow .16s ease;
}
.ts-input::placeholder{color:var(--ts-muted)}
.ts-input:focus,.ts-select:focus{
  outline:none;border-color:var(--ts-accent);
  box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-accent) 24%,transparent);
}
.ts-input[aria-invalid="true"]{border-color:var(--ts-danger)}
.ts-select{
  padding-right:44px;
  background-image:linear-gradient(45deg,transparent 50%,currentColor 50%),linear-gradient(135deg,currentColor 50%,transparent 50%);
  /* Centred rather than pinned 24px from the top, so the chevron stays put
     when the select is stretched to match a taller neighbouring control. */
  background-position:calc(100% - 22px) calc(50% - 1px),calc(100% - 17px) calc(50% - 1px);
  background-size:5px 5px,5px 5px;background-repeat:no-repeat;
}
.ts-select option{background:#0b1222;color:#e8eefb}
[data-theme="light"] .ts-select option{background:#fff;color:#0b1220}
.ts-mono{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;word-break:break-all}

.ts-btn{
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  min-height:52px;padding:13px 24px;
  font-family:inherit;font-size:.95rem;font-weight:700;
  border-radius:14px;border:1px solid var(--ts-border);
  background:var(--ts-surface-2);color:var(--ts-text);
  cursor:pointer;white-space:nowrap;
  transition:border-color .16s ease,transform .1s ease,opacity .16s ease;
}
.ts-btn:hover:not(:disabled){border-color:var(--ts-border-strong)}
.ts-btn:active:not(:disabled){transform:translateY(1px)}
.ts-btn:disabled{opacity:.5;cursor:not-allowed}
.ts-btn-primary{
  border-color:transparent;color:#04121a;font-weight:800;
  background:linear-gradient(100deg,var(--ts-accent),var(--ts-accent-2));
  box-shadow:0 16px 34px -18px rgba(16,185,129,.8);
}
[data-theme="light"] .ts-btn-primary{color:#fff}
.ts-btn-sm{min-height:42px;padding:9px 16px;font-size:.84rem;border-radius:11px}

.ts-chip{
  display:inline-flex;align-items:center;gap:7px;
  padding:6px 13px;border-radius:999px;
  font-size:.72rem;font-weight:750;letter-spacing:.08em;text-transform:uppercase;
  border:1px solid var(--ts-border);background:var(--ts-surface-2);color:var(--ts-muted);
}
.ts-chip[data-tone="ok"]{color:var(--ts-ok);border-color:color-mix(in srgb,var(--ts-ok) 42%,transparent)}
.ts-chip[data-tone="warn"]{color:var(--ts-warn);border-color:color-mix(in srgb,var(--ts-warn) 42%,transparent)}
.ts-chip[data-tone="danger"]{color:var(--ts-danger);border-color:color-mix(in srgb,var(--ts-danger) 42%,transparent)}
.ts-chip[data-tone="accent"]{color:var(--ts-accent);border-color:var(--ts-border-strong)}

/* Risk dial */
.ts-dial{position:relative;width:170px;height:170px;flex:0 0 auto;margin-inline:auto}
.ts-dial svg{transform:rotate(-90deg);display:block}
.ts-dial-track{stroke:var(--ts-border);fill:none}
.ts-dial-value{fill:none;stroke-linecap:round;transition:stroke-dashoffset .8s cubic-bezier(.4,0,.2,1)}
.ts-dial-centre{
  position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
  text-align:center;gap:2px;
}
.ts-dial-num{font-size:2.5rem;font-weight:800;line-height:1;color:var(--ts-text);font-variant-numeric:tabular-nums}
.ts-dial-cap{font-size:.62rem;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:var(--ts-muted)}

.ts-stat{padding:16px 18px;border-radius:16px;border:1px solid var(--ts-border);background:var(--ts-surface-2)}
.ts-stat-label{font-size:.63rem;font-weight:800;letter-spacing:.15em;text-transform:uppercase;color:var(--ts-muted)}
.ts-stat-value{margin-top:7px;font-size:1.6rem;font-weight:800;line-height:1.1;color:var(--ts-text);font-variant-numeric:tabular-nums}
.ts-stat-hint{margin-top:4px;font-size:.74rem;line-height:1.45;color:var(--ts-muted)}

.ts-row{
  display:flex;flex-wrap:wrap;align-items:flex-start;justify-content:space-between;gap:10px;
  padding:13px 0;border-bottom:1px solid var(--ts-border);
}
.ts-row:last-child{border-bottom:0}
.ts-row-k{font-size:.78rem;font-weight:700;color:var(--ts-text-soft)}
.ts-row-v{font-size:.82rem;color:var(--ts-muted);text-align:right;min-width:0;word-break:break-word}

.ts-alert{
  display:flex;flex-wrap:wrap;align-items:center;gap:12px;
  padding:14px 18px;border-radius:16px;font-size:.88rem;line-height:1.55;
  border:1px solid var(--ts-border);background:var(--ts-surface-2);color:var(--ts-text-soft);
}
.ts-alert[data-tone="danger"]{border-color:color-mix(in srgb,var(--ts-danger) 45%,transparent);color:var(--ts-danger)}
.ts-alert[data-tone="warn"]{border-color:color-mix(in srgb,var(--ts-warn) 45%,transparent);color:var(--ts-warn)}

.ts-skel{border-radius:16px;background:var(--ts-surface-2);animation:ts-pulse 1.4s ease-in-out infinite}
@keyframes ts-pulse{0%,100%{opacity:.5}50%{opacity:.85}}

.ts-hint{font-size:.78rem;line-height:1.5;color:var(--ts-muted);margin-top:7px}
.ts-error{font-size:.78rem;line-height:1.5;color:var(--ts-danger);margin-top:7px;font-weight:650}

@media (max-width:639.98px){
  .ts-dial{width:142px;height:142px}
  .ts-dial-num{font-size:2.1rem}
  .ts-btn{width:100%}
  /* At narrow widths a long value wraps under its label and lands
     left-aligned while short values stay right-aligned, so the column reads
     as ragged. Stack every pair instead - one consistent shape. */
  .ts-row{flex-direction:column;gap:3px}
  .ts-row-v{text-align:left;width:100%}
}

@media (prefers-reduced-motion:reduce){
  .ts-root *,.ts-root *::before,.ts-root *::after{
    animation-duration:.001ms !important;transition-duration:.001ms !important;
  }
}

/* ── Search-specific pieces ───────────────────────────────────────────── */
/* The query row was a flex row with a min-width:0 field, an 11rem chain
   column and a button. Between roughly 900px and 1150px the uppercase,
   letter-spaced labels ("ADDRESS, DOMAIN OR TRANSACTION" / "CHAIN") are wider
   than the columns that hold them and cannot wrap, so they overhang and read
   as colliding with each other. A grid gives each control a declared track
   instead of letting the labels drive the width, and the labels are allowed to
   wrap rather than overflow. */
.ts-search{display:grid;gap:14px;grid-template-columns:1fr}
.ts-search > *{min-width:0}
.ts-search .ts-label{white-space:normal;overflow-wrap:anywhere}
/* Tablet: query spans the row, chain and submit share the one below. */
@media (min-width:640px){
  .ts-search{grid-template-columns:minmax(0,1fr) minmax(9rem,auto);align-items:end;column-gap:16px}
  .ts-search-field{grid-column:1 / -1}
}
/* Desktop: one clean row, generous gutter between the query and the chain. */
@media (min-width:1024px){
  .ts-search{grid-template-columns:minmax(0,1fr) 13.5rem auto;column-gap:20px}
  .ts-search-field{grid-column:auto}
}
.ts-search-field{min-width:0}
.ts-search .ts-btn{width:100%}
@media (min-width:1024px){.ts-search .ts-btn{width:auto;min-width:8rem}}
/* Once the controls share a row they are bottom-aligned, so a 52px select next
   to a 60px query input pushes its own label 8px lower than the query label -
   the two headings sit at different heights and the row reads as crooked.
   Matching the control heights makes the labels share a baseline as well. */
@media (min-width:640px){
  .ts-search .ts-select,.ts-search .ts-btn{min-height:60px;border-radius:16px}
}
.ts-input-lg{min-height:60px;padding:16px 20px;font-size:1.05rem;border-radius:16px}
.ts-input-sm{min-height:44px;padding:10px 13px;font-size:.9rem;border-radius:12px}
.ts-result{
  display:flex;flex-wrap:wrap;align-items:center;gap:16px;
  padding:18px;border-radius:18px;
  border:1px solid var(--ts-border);background:var(--ts-surface-2);
  transition:border-color .16s ease;
}
.ts-result:hover{border-color:var(--ts-border-strong)}
.ts-avatar{
  width:52px;height:52px;flex:0 0 auto;border-radius:14px;overflow:hidden;
  border:1px solid var(--ts-border);background:var(--ts-surface);
  display:grid;place-items:center;
}
.ts-avatar img{width:100%;height:100%;object-fit:cover;display:block}
.ts-result-main{flex:1 1 16rem;min-width:0}
.ts-result-name{font-size:1rem;font-weight:750;color:var(--ts-text);margin:0;word-break:break-word}
.ts-result-actions{display:flex;flex-wrap:wrap;gap:8px;flex:0 0 auto}
@media (max-width:639.98px){
  .ts-result{padding:15px}
  .ts-result-actions{width:100%}
  .ts-result-actions .ts-btn{flex:1 1 auto}
}
.ts-score{display:inline-flex;align-items:baseline;gap:5px;font-variant-numeric:tabular-nums}
.ts-score b{font-size:1.1rem;font-weight:800;color:var(--ts-text);line-height:1}
.ts-score span{font-size:.66rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--ts-muted)}
/* ── Portalled combobox (ProviderSelect) ──────────────────────────────────
   Rendered into document.body, so it sits outside .ts-root and cannot inherit
   the --ts-* tokens. It previously shipped as bg-white ... border-gray-200,
   a white panel on a dark page. Colours are declared explicitly here, with the
   light variant keyed off <html data-theme>, which does reach a portal. */
.ts-portal{
  /* Tells the engine to paint the native scrollbar in the panel's own scheme,
     so a dark dropdown does not get a light OS scrollbar down its edge. */
  color-scheme:dark;
  border:1px solid rgba(158,178,214,.22);
  border-radius:14px;
  background:#111a30;
  color:#EAF0FF;
  box-shadow:0 26px 60px -28px rgba(0,0,0,.9);
  overflow:hidden;
  display:flex;flex-direction:column;
}
[data-theme="light"] .ts-portal{
  color-scheme:light;
  border-color:rgba(15,23,42,.14);
  background:#ffffff;
  color:#0B1220;
  box-shadow:0 20px 44px -26px rgba(15,23,42,.35);
}
.ts-portal-list{
  /* flex:1 + min-height:0 lets the list take whatever height is left after the
     pinned footer, instead of guessing the footer's height in JS. */
  flex:1 1 auto;min-height:0;
  overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;
  /* scrollbar-width is deliberately NOT set: once it is, Chromium switches to
     the standard overlay scrollbar and ignores ::-webkit-scrollbar, so the bar
     only appears mid-gesture and nothing tells the user the list scrolls at
     all. Leaving it unset keeps the always-reserved 10px bar below.
     scrollbar-color still styles Firefox, which has no webkit pseudo. */
  scrollbar-color:rgba(158,178,214,.45) transparent;
  scrollbar-gutter:stable;
}
.ts-portal-list::-webkit-scrollbar{width:10px}
.ts-portal-list::-webkit-scrollbar-thumb{
  background:rgba(158,178,214,.4);border-radius:999px;
  border:3px solid transparent;background-clip:content-box;
}
[data-theme="light"] .ts-portal-list::-webkit-scrollbar-thumb{background:rgba(15,23,42,.28);background-clip:content-box}
.ts-portal-item{
  display:block;width:100%;text-align:left;
  padding:11px 14px;border:0;background:transparent;color:inherit;
  font-family:inherit;cursor:pointer;
}
.ts-portal-item:hover,.ts-portal-item[data-active="true"]{background:rgba(255,255,255,.07)}
[data-theme="light"] .ts-portal-item:hover,[data-theme="light"] .ts-portal-item[data-active="true"]{background:rgba(15,23,42,.05)}
.ts-portal-item b{display:block;font-size:.88rem;font-weight:650;line-height:1.3}
.ts-portal-item small{display:block;margin-top:2px;font-size:.74rem;opacity:.65;line-height:1.35}
.ts-portal-note{padding:12px 14px;font-size:.82rem;opacity:.7}
/* Kept out of the scroll container so "add a new provider" stays reachable
   without scrolling to the bottom of a long list. */
.ts-portal-foot{
  flex:0 0 auto;padding:10px 12px;
  border-top:1px solid rgba(158,178,214,.2);
  background:inherit;
}
[data-theme="light"] .ts-portal-foot{border-top-color:rgba(15,23,42,.12)}
.ts-portal-foot button{
  width:100%;min-height:40px;padding:9px 14px;
  font-family:inherit;font-size:.82rem;font-weight:700;
  border-radius:10px;border:1px solid rgba(0,245,255,.32);
  background:transparent;color:#7ff3ff;cursor:pointer;
}
[data-theme="light"] .ts-portal-foot button{border-color:rgba(12,123,214,.4);color:#0C7BD6}

.ts-details > summary{
  cursor:pointer;list-style:none;padding:4px 0;
  display:flex;align-items:center;justify-content:space-between;gap:12px;
}
.ts-details > summary::-webkit-details-marker{display:none}
.ts-details > summary::after{
  content:"";width:9px;height:9px;flex:0 0 auto;
  border-right:2px solid var(--ts-muted);border-bottom:2px solid var(--ts-muted);
  transform:rotate(45deg);transition:transform .2s ease;
}
.ts-details[open] > summary::after{transform:rotate(-135deg)}
`;

export default function TrustSurface() {
  return <style dangerouslySetInnerHTML={{ __html: TRUST_CSS }} />;
}
